import React, { useMemo } from "react";
import { Autocomplete, TextField, Checkbox, Chip, Box } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import globals from "../Globals";

// A multi-select of ISO alpha-2 countries: toggles in the dropdown, chips for the picked ones, type to filter by
// code or name. The codes are the backend's CountryMapping list (globals.countryCodes); the names come from the
// browser (Intl.DisplayNames), so nothing is duplicated. `value` / `onChange` are arrays of codes.
const names = (() => { try { return new Intl.DisplayNames(["en"], { type: "region" }); } catch { return null; } })();
export const countryName = code => { try { return names?.of(code) ?? code; } catch { return code; } };

export default function CountryPicker({ value, onChange, label = "Countries", helperText, size = "small", sx, placeholder = "Everywhere" }) {
    const options = useMemo(() => [...globals.countryCodes].sort((a, b) => countryName(a).localeCompare(countryName(b))), []);
    const selected = (value ?? []).filter(c => options.includes(c));
    return (
        <Autocomplete
            multiple disableCloseOnSelect size={size} options={options} value={selected}
            onChange={(e, v) => onChange(v)}
            getOptionLabel={code => `${countryName(code)} (${code})`}
            filterOptions={(opts, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                return q ? opts.filter(c => c.toLowerCase().startsWith(q) || countryName(c).toLowerCase().includes(q)) : opts;
            }}
            renderOption={(props, code, { selected }) => {
                const { key, ...rest } = props;
                return (
                    <li key={key} {...rest}>
                        <Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} checked={selected} sx={{ mr: 1, p: 0 }} />
                        <Box component="span" sx={{ flex: 1 }}>{countryName(code)}</Box>
                        <Box component="span" sx={{ color: "text.secondary", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>{code}</Box>
                    </li>
                );
            }}
            renderValue={(vals, getTagProps) => vals.map((code, i) => { const { key, ...tag } = getTagProps({ index: i }); return <Chip key={key} size="small" label={code} title={countryName(code)} {...tag} />; })}
            renderInput={params => <TextField {...params} label={label} placeholder={selected.length ? "" : placeholder} helperText={helperText} />}
            sx={sx}
        />
    );
}
